import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  FiTrash2,
  FiLoader,
  FiCheck,
  FiPlus,
  FiFile,
  FiFilm,
  FiX,
  FiUsers,
  FiLayers,
  FiAlertCircle,
  FiUserCheck,
  FiChevronLeft,
} from 'react-icons/fi';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import dicomParser from 'dicom-parser';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { SWEEPS } from './constants';
import {
  applyDatingFromTotalDays,
  calculateGA,
  computeGAFromLmp,
  deriveTotalDaysFromBiometryForm,
  getVideoMetadata,
  isLmpPlausibleAtReference,
} from './utils';
import type { PregnancyResponse } from '../../../helpers/api/pregnancy/pregnancy';

const GA_LIMITS = { MAX_TOTAL: 44 };
import { useUnifiedUpload } from '../../../helpers/api/UnifiedUpload/useUnifiedUpload';
import { PatientIdentityForm } from '../PatientIdentityForm';
import { PregnancyForm } from '../PregnancyForm';
import { FileWorkspace } from '../FileWorkspace';
import { DicomViewer, VideoViewer } from '../Viewers/Viewers';
import { usePaginatedPatients } from '../../../helpers/api/patients/patients';
import { useGetLatestPregnancy } from '../../../helpers/api/pregnancy/pregnancy';
import type { Patient } from '../../interfaces/patient';

import cornerstone from 'cornerstone-core';
import cornerstoneMath from 'cornerstone-math';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import Hammer from 'hammerjs';
import {
  init as cornerstoneToolsInit,
  addTool,
  StackScrollMouseWheelTool,
  addStackStateManager,
  external as csToolsExternal,
} from 'cornerstone-tools';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

try {
  csToolsExternal.cornerstone = cornerstone;
  csToolsExternal.Hammer = Hammer;
  csToolsExternal.cornerstoneMath = cornerstoneMath;
  cornerstoneToolsInit();
  const stackModule = cornerstoneToolsInit.store?.modules?.stackState;
  if (stackModule) addStackStateManager(stackModule, ['stack']);
  addTool(StackScrollMouseWheelTool);
  cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
  cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
  cornerstoneWADOImageLoader.configure({
    useWebWorkers: true,
    decodeConfig: { convertFloatPixelDataToInt: false },
  });
  if (cornerstoneWADOImageLoader.wadouri) {
    cornerstone.registerImageLoader(
      'dicomfile',
      cornerstoneWADOImageLoader.wadouri.loadImage
    );
  }
} catch (e) {
  console.warn('Cornerstone init warning:', e);
}

const BatchDecisionOverlay = ({
  uniquePatientIds,
  onSeparate,
  onRegroup,
  t,
}: any) => (
  <div className="absolute inset-0 z-50 bg-white/95 dark:bg-gray-900/95 flex flex-col items-center justify-center p-8 backdrop-blur-sm animate-in fade-in duration-200 rounded-xl">
    <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full text-center">
      <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
        <FiUsers />
      </div>
      <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
        {t('oneStep.batch.multiplePatientsDetected')}
      </h3>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        {t('oneStep.batch.weDetected')}{' '}
        <span className="font-bold text-gray-900 dark:text-gray-200">
          {uniquePatientIds.length} {t('oneStep.batch.differentPatientIds')}
        </span>
        .
      </p>
      <div className="space-y-3">
        <button
          onClick={onSeparate}
          className="w-full p-4 rounded-xl border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 text-left flex items-center gap-4 transition-all group"
        >
          <div className="bg-blue-500 text-white p-3 rounded-lg shadow-sm group-hover:scale-105 transition-transform">
            <FiLayers size={24} />
          </div>
          <div>
            <div className="font-bold text-blue-900 dark:text-blue-300 text-lg">
              {t('oneStep.batch.separateCases')}
            </div>
            <div className="text-xs text-blue-700 dark:text-blue-400">
              {t('oneStep.batch.separateCasesHelp')}
            </div>
          </div>
        </button>
        <button
          onClick={onRegroup}
          className="w-full p-4 rounded-xl border-2 border-gray-100 dark:border-gray-700 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 text-left flex items-center gap-4 transition-all group"
        >
          <div className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 p-3 rounded-lg group-hover:bg-amber-400 group-hover:text-white transition-colors">
            <FiUsers size={24} />
          </div>
          <div>
            <div className="font-bold text-gray-800 dark:text-gray-200 group-hover:text-amber-900 dark:group-hover:text-amber-100 text-lg">
              {t('oneStep.batch.regroupAll')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-amber-800">
              {t('oneStep.batch.regroupAllHelp')}
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>
);

type NewCaseWizardOneStepProps = {
  /** When set, renders as a modal overlay. Omit on `/new-case` route. */
  isOpen?: boolean;
  onClose?: () => void;
  onCaseCreated?: () => void;
  /** When set (e.g. opened from a patient profile), patient search / create / change are disabled. */
  contextPatient?: Patient | (Patient & { patient_id?: string }) | null;
};

export const NewCaseWizard = ({
  isOpen: isOpenProp,
  onClose: onCloseProp,
  onCaseCreated,
  contextPatient: contextPatientProp = null,
}: NewCaseWizardOneStepProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isModalMode = isOpenProp !== undefined || onCloseProp !== undefined;
  const isOpen = isOpenProp ?? true;
  const onClose = onCloseProp ?? (() => navigate(-1));
  const contextPatient =
    contextPatientProp ??
    (location.state as { contextPatient?: Patient } | null)?.contextPatient ??
    null;

  const { t } = useTranslation('NewCaseWizard');
  const queryClient = useQueryClient();
  const obInputRef = useRef<HTMLInputElement>(null);
  const blindInputRef = useRef<HTMLInputElement>(null);
  const [activeUploadSlot, setActiveUploadSlot] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'OB' | 'BLIND'>('OB');
  const [obFiles, setObFiles] = useState<any[]>([]);
  const [blindAssignments, setBlindAssignments] = useState<Record<number, any>>(
    {}
  );
  const [batchMode, setBatchMode] = useState<
    'NONE' | 'DECIDING' | 'SEPARATE' | 'REGROUP'
  >('NONE');

  const [uniquePatientIds, setUniquePatientIds] = useState<string[]>([]);
  const [patientMap, setPatientMap] = useState<Record<string, string>>({});
  const [uniqueDates, setUniqueDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const [targetPatientId, setTargetPatientId] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<any | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>(
    {}
  );

  const [patForm, setPatForm] = useState({
    firstName: '',
    lastName: '',
    id: '',
  });
  const [trimester, setTrimester] = useState<any>('T1');
  const [pregForm, setPregForm] = useState({
    lmp: null as dayjs.Dayjs | null,
    edd: null as dayjs.Dayjs | null,
    gaWeeks: '',
    gaDays: '',
    crl: '',
    bpd: '',
    hc: '',
    ac: '',
    fl: '',
    serverGaCrl: null,
    serverGaBpd: null,
    serverGaHc: null,
    serverGaAc: null,
    serverGaFl: null,
  });
  const [isLocked, setIsLocked] = useState(false);
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  const lastPregnancyEditRef = useRef<'lmp' | 'edd' | 'ga' | 'biometry' | null>(
    null
  );

  const lockPatientIdentity = !!contextPatient;

  const applyContextPatient = useCallback(() => {
    if (!contextPatient) return;
    const p = contextPatient as Patient & { patient_id?: string };
    setSelectedPatient({
      ...p,
      id: p.id as never,
    });
    setIsCreatingNew(false);
    setSearchTerm('');
    setPatForm({
      firstName: p.patient_name || '',
      lastName: p.patient_last_name || '',
      id: String(p.patient_id ?? p.id ?? ''),
    });
  }, [contextPatient]);

  useLayoutEffect(() => {
    if (!contextPatient) return;
    applyContextPatient();
  }, [contextPatient, applyContextPatient]);

  const obTypeLock = useMemo(
    () => (obFiles.length > 0 ? obFiles[0].type : null),
    [obFiles]
  );
  const blindTypeLock = useMemo(() => {
    const files = Object.values(blindAssignments);
    return files.length > 0 ? files[0].type : null;
  }, [blindAssignments]);
  const handleBlindClick = (sweepId: number) => {
    setActiveUploadSlot(sweepId); // Remember which slot was clicked
    blindInputRef.current?.click(); // Open the file dialog
  };
  const { filteredObFiles, filteredBlindAssignments } = useMemo(() => {
    if (batchMode === 'REGROUP' || batchMode === 'NONE') {
      return {
        filteredObFiles: obFiles,
        filteredBlindAssignments: blindAssignments,
      };
    }
    const filteredOb = obFiles.filter(
      (f: any) =>
        !f.dicomData?.patientId || f.dicomData.patientId === targetPatientId
    );
    const filteredBlind: Record<number, any> = {};
    Object.entries(blindAssignments).forEach(([key, f]: [string, any]) => {
      if (
        !f.dicomData?.patientId ||
        f.dicomData.patientId === targetPatientId
      ) {
        filteredBlind[parseInt(key)] = f;
      }
    });
    return {
      filteredObFiles: filteredOb,
      filteredBlindAssignments: filteredBlind,
    };
  }, [obFiles, blindAssignments, batchMode, targetPatientId]);

  const obStudyDateFormatted = useMemo(() => {
    const raw = filteredObFiles.find((f: any) => f.dicomData?.studyDate)
      ?.dicomData?.studyDate;
    if (!raw || String(raw).length !== 8) return null;
    const d = dayjs(String(raw), 'YYYYMMDD');
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  }, [filteredObFiles]);

  const buildPatientSearchFilters = useCallback((term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return {};
    const pattern = trimmed.includes('%') ? trimmed : `%${trimmed}%`;
    return {
      filters: JSON.stringify({
        condition__or: {
          patient_id__ilike: pattern,
          patient_name__ilike: pattern,
          patient_last_name__ilike: pattern,
        },
      }),
    };
  }, []);

  const onSearch = useCallback(
    debounce((val: string) => setSearchTerm(val), 400),
    []
  );
  const { data: patientList, isFetching: isSearchingPatients } =
    usePaginatedPatients(
      1,
      50,
      buildPatientSearchFilters(searchTerm),
      {
        enabled: isModalMode ? isOpen : true,
        queryKey: ['patientSearch', searchTerm],
      }
    );

  const { data: pregData } = useGetLatestPregnancy(selectedPatient?.id, {
    enabled: !!selectedPatient?.id,
  });

  const getPregnancyRecord = useCallback((): PregnancyResponse['pregnancy'] => {
    const raw = pregData as PregnancyResponse | { data?: PregnancyResponse } | undefined;
    if (!raw) return null;
    if ('pregnancy' in raw && raw.pregnancy !== undefined) return raw.pregnancy;
    if ('data' in raw && raw.data?.pregnancy !== undefined) return raw.data.pregnancy;
    return null;
  }, [pregData]);

  /** Scan/exam date from uploaded files (always the real exam date for storage). */
  const getExamDateDayjs = useCallback(() => {
    if (selectedDate?.isValid()) return selectedDate.startOf('day');
    if (obStudyDateFormatted) {
      const fromStudy = dayjs(obStudyDateFormatted).startOf('day');
      if (fromStudy.isValid()) return fromStudy;
    }
    const pregnancy = getPregnancyRecord();
    if (pregnancy?.ultrasound_date) {
      const u = dayjs(pregnancy.ultrasound_date).startOf('day');
      if (u.isValid()) return u;
    }
    return dayjs().startOf('day');
  }, [selectedDate, obStudyDateFormatted, getPregnancyRecord]);

  /** Reference for LMP / EDD / GA sync — always the exam date. */
  const getExamDateStr = useCallback(
    () => getExamDateDayjs().format('YYYY-MM-DD'),
    [getExamDateDayjs]
  );

  const isExamOverOneYearOld = useMemo(
    () => dayjs().diff(getExamDateDayjs(), 'day') > 365,
    [getExamDateDayjs]
  );

  const isHistoricalDatingMode = isExamOverOneYearOld;
  const isLmpDisabledByDate = isHistoricalDatingMode;

  /** In historical mode, show read-only LMP/EDD derived from biometry or GA at exam date. */
  useEffect(() => {
    if (!isHistoricalDatingMode) return;

    setPregForm((prev) => {
      const bioTotal = deriveTotalDaysFromBiometryForm(prev);
      let totalDays: number | null = bioTotal;
      if (totalDays === null) {
        const w = parseInt(prev.gaWeeks || '0', 10);
        const d = parseInt(prev.gaDays || '0', 10);
        const fromGa = w * 7 + d;
        totalDays = fromGa > 0 ? fromGa : null;
      }

      if (totalDays === null || totalDays <= 0) {
        if (!prev.lmp && !prev.edd) return prev;
        return { ...prev, lmp: null, edd: null };
      }

      const dating = applyDatingFromTotalDays(totalDays, getExamDateDayjs());
      if (
        prev.lmp?.isSame(dating.lmp, 'day') &&
        prev.edd?.isSame(dating.edd, 'day')
      ) {
        return prev;
      }
      return { ...prev, lmp: dating.lmp, edd: dating.edd };
    });
  }, [
    isHistoricalDatingMode,
    pregForm.crl,
    pregForm.bpd,
    pregForm.hc,
    pregForm.ac,
    pregForm.fl,
    pregForm.gaWeeks,
    pregForm.gaDays,
    getExamDateDayjs,
  ]);

  const switchToPatientContext = (id: string, map = patientMap) => {
    if (batchMode === 'REGROUP') {
      setTargetPatientId(null);
    } else {
      setTargetPatientId(id);
    }
    const pName = map[id] || '';
    const nameParts = pName.split(' ');
    setPatForm({
      firstName: nameParts.slice(1).join(' '),
      lastName: nameParts[0] || '',
      id: id,
    });
    setFieldErrors({});

    if (lockPatientIdentity && contextPatient) {
      setSearchTerm(id);
      return;
    }

    setSelectedPatient(null);
    setIsLocked(false);
    // Keep search visible: look up by DICOM ID first; only create-new if no match.
    setIsCreatingNew(false);
    setSearchTerm(id);
  };

  useEffect(() => {
    if (!patientList?.items || isSearchingPatients) return;
    const idToMatch = targetPatientId || patForm.id;
    if (idToMatch && searchTerm === idToMatch) {
      const match = patientList.items.find(
        (p: any) => String(p.patient_id) === String(idToMatch)
      );
      if (match && selectedPatient?.id !== match.id) {
        setSelectedPatient(match);
        setIsCreatingNew(false);
        return;
      }
      if (
        !selectedPatient &&
        !isCreatingNew &&
        patientList.items.length === 0
      ) {
        setIsCreatingNew(true);
        return;
      }
    }
    if (patForm.firstName?.trim() && patForm.lastName?.trim()) {
      const match = patientList.items.find(
        (p: any) =>
          (p.patient_name || '').toLowerCase() ===
            patForm.firstName.trim().toLowerCase() &&
          (p.patient_last_name || '').toLowerCase() ===
            patForm.lastName.trim().toLowerCase()
      );
      if (match) {
        setSelectedPatient(match);
        setIsCreatingNew(false);
        setSearchTerm('');
      }
    }
  }, [
    patientList,
    targetPatientId,
    patForm.id,
    searchTerm,
    selectedPatient,
    isCreatingNew,
    isSearchingPatients,
    patForm.firstName,
    patForm.lastName,
  ]);

  const handleRemoveOb = (fileId: string) =>
    setObFiles((prev) => prev.filter((f) => f.id !== fileId));
  const handleRemoveBlind = (sweepId: number) =>
    setBlindAssignments((prev) => {
      const next = { ...prev };
      delete next[sweepId];
      return next;
    });
  const handleBlindDrop = (sourceId: number, targetId: number) => {
  // 1. Don't do anything if dropped on itself
  if (sourceId === targetId) return;

  setBlindAssignments((prev) => {
    const next = { ...prev };
    
    // 2. Store the files from both slots
    const sourceFile = next[sourceId];
    const targetFile = next[targetId];

    // 3. Perform the swap
    next[targetId] = sourceFile;
    
    // If target was empty, source becomes null/undefined. 
    // If target had a file, it moves to the source slot.
    if (targetFile) {
        next[sourceId] = targetFile;
    } else {
        delete next[sourceId]; 
    }

    return next;
  });
};

  const processFile = async (file: File): Promise<any> => {
    const isDicom = file.name.toLowerCase().endsWith('.dcm');
    const isVideo = file.type.startsWith('video/');

    if (!isDicom && !isVideo) return null;

    let uid = file.name + '-' + file.size;
    const id = uid;

    let videoMeta, dicomData;
    if (isDicom) {
      try {
        const headerBlob = file.slice(0, 5000000);
        const buffer = await headerBlob.arrayBuffer();
        let ds;
        try {
          ds = dicomParser.parseDicom(new Uint8Array(buffer));
        } catch (err: any) {
          if (err.dataSet) ds = err.dataSet;
          else throw err;
        }

        if (ds) {
          const rawName = ds.string('x00100010') || '';
          const pid = ds.string('x00100020') || 'unknown';
          const studyDateStr = ds.string('x00080020');
          const sopUid = ds.string('x00080018');

          if (sopUid) uid = sopUid;

          let formattedDate = null;
          if (studyDateStr && studyDateStr.length === 8) {
            const d = dayjs(studyDateStr, 'YYYYMMDD');
            if (d.isValid()) formattedDate = d.format('DD/MM/YYYY');
          }

          if (pid !== 'unknown') {
            dicomData = {
              patientName: rawName.replace(/\^/g, ' ').trim(),
              patientId: pid,
              studyDate: studyDateStr || '',
              formattedDate,
              sopInstanceUid: sopUid,
            };
          }
        }
      } catch (e) {
        console.warn('Failed to parse DICOM header:', e);
      }
    } else {
      videoMeta = await getVideoMetadata(file);
    }

    return {
      id,
      uid,
      file,
      type: isDicom ? 'dicom' : 'video',
      videoMeta,
      dicomData,
    };
  };

  const handleUpload = async (files: File[], targetTab: 'OB' | 'BLIND') => {
    const newItems: any[] = [];
    const currentLock = targetTab === 'OB' ? obTypeLock : blindTypeLock;
    
    const existingUids = new Set([
      ...obFiles.map((f) => f.uid),
      ...Object.values(blindAssignments).map((f: any) => f.uid),
    ]);

    let invalidCount = 0;
    let duplicateCount = 0;
    let mismatchCount = 0;
    let noSpaceCount = 0;

    const availableSlots = targetTab === 'BLIND' 
      ? 7 - Object.keys(blindAssignments).length 
      : 999;

    for (const file of files) {
      const item = await processFile(file);

      // 1. Check for valid format
      if (!item) {
        invalidCount++;
        continue;
      }

      if (existingUids.has(item.uid)) {
        duplicateCount++;
        continue;
      }

      const effectiveLock = currentLock || (newItems.length > 0 ? newItems[0].type : item.type);
      if (item.type !== effectiveLock) {
        mismatchCount++;
        continue;
      }

      if (targetTab === 'BLIND' && newItems.length >= availableSlots) {
        noSpaceCount++;
        continue;
      }

      newItems.push(item);
    }

    // --- Show simple alerts based on results ---
    if (invalidCount > 0) {
      messageI18n.error('newCase.filesInvalidFormat', { count: invalidCount });
    }
    if (duplicateCount > 0) {
      messageI18n.warning('newCase.filesDuplicateSkipped', { count: duplicateCount });
    }
    if (mismatchCount > 0) {
      messageI18n.error('newCase.filesMixRejected', { count: mismatchCount });
    }
    if (noSpaceCount > 0) {
      messageI18n.warning('newCase.filesNoSpaceBlind', { count: noSpaceCount });
    }

    if (obInputRef.current) obInputRef.current.value = '';
    if (blindInputRef.current) blindInputRef.current.value = '';

    if (newItems.length === 0) return;

    if (targetTab === 'OB') {
      setObFiles((prev) => [...prev, ...newItems]);
    } else {
      setBlindAssignments((prev) => {
        const next = { ...prev };
        let itemIdx = 0;
        for (let i = 1; i <= 7; i++) {
          if (itemIdx >= newItems.length) break;
          if (!next[i]) {
            next[i] = newItems[itemIdx];
            itemIdx++;
          }
        }
        return next;
      });
    }
    setActiveTab(targetTab);
  };
  useEffect(() => {
    const allFiles = [...obFiles, ...Object.values(blindAssignments)];
    const ids = new Set<string>();
    const dates = new Set<string>();
    const pMap: Record<string, string> = {};
    allFiles.forEach((f: any) => {
      if (f.dicomData) {
        if (f.dicomData.patientId) {
          ids.add(f.dicomData.patientId);
          pMap[f.dicomData.patientId] = f.dicomData.patientName;
        }
        if (f.dicomData.studyDate) dates.add(f.dicomData.studyDate);
      }
    });
    const idArray = Array.from(ids);
    setUniquePatientIds(idArray);
    setPatientMap(pMap);
    setUniqueDates(Array.from(dates));

    if (idArray.length > 1 && batchMode === 'NONE') setBatchMode('DECIDING');
    else if (idArray.length <= 1 && batchMode === 'DECIDING')
      setBatchMode('NONE');

    if (
      batchMode === 'SEPARATE' ||
      (batchMode === 'NONE' && idArray.length > 0)
    ) {
      if (!targetPatientId || !ids.has(targetPatientId)) {
        if (idArray.length > 0) switchToPatientContext(idArray[0], pMap);
      }
    }
    if (Array.from(dates).length > 0) {
      const sorted = Array.from(dates).sort((a, b) => b.localeCompare(a));
      const best = dayjs(sorted[0], 'YYYYMMDD');
      if (best.isValid()) setSelectedDate(best);
    }
  }, [obFiles, blindAssignments, batchMode]);

  const { mutate: uploadCase, isPending: isUploading } = useUnifiedUpload({
    onSuccess: (data) => {
      const successKeys = new Set(data.successKeys);

      const fileKey = (f: any) => f?.uid ?? f?.file?.name ?? '';

      const remainingObFiles = obFiles.filter((f) => !successKeys.has(fileKey(f)));
      const remainingBlindAssignments = { ...blindAssignments };
      Object.keys(remainingBlindAssignments).forEach((key) => {
        if (successKeys.has(fileKey(remainingBlindAssignments[key])))
          delete remainingBlindAssignments[key];
      });

      setObFiles(remainingObFiles);
      setBlindAssignments(remainingBlindAssignments);

      if (data.failed.length === 0) {
        handleResetFormOnly();
      }

      if (batchMode === 'SEPARATE') {
        const nextFile =
          remainingObFiles.find((f: any) => f.dicomData?.patientId) ||
          Object.values(remainingBlindAssignments).find(
            (f: any) => f.dicomData?.patientId
          );

        if (nextFile) {
          const nextId = nextFile.dicomData.patientId;
          const nextName = nextFile.dicomData.patientName || '';

          messageI18n.success('newCase.uploadSuccessSwitchingPatient', {
            name: nextName ? nextName : nextId,
          });

          setTargetPatientId(nextId);

          const nameParts = nextName.split(' ');
          setPatForm({
            firstName: nameParts.slice(1).join(' '),
            lastName: nameParts[0] || '',
            id: nextId,
          });

          setIsCreatingNew(true);
          setSearchTerm(nextId);
        } else {
          const totalRemaining =
            remainingObFiles.length +
            Object.keys(remainingBlindAssignments).length;
          if (totalRemaining === 0) {
            messageI18n.success('upload.complete');
            handleClose();
            if (onCaseCreated) onCaseCreated();
          } else {
            messageI18n.success('upload.caseUploaded');
          }
        }
      } else {
        if (data.failed.length === 0) {
          messageI18n.success('upload.complete');
          handleClose();
          if (onCaseCreated) onCaseCreated();
        }
      }
    },
    onError: (error: any) => {
      const resData = error;
      const errorDetails = resData?.details || resData?.detail || '';

      if (Array.isArray(errorDetails)) {
        const newFieldErrors: Record<string, string> = {};
        const msgList: string[] = [];

        errorDetails.forEach((err: any) => {
          const fieldName = err.loc[err.loc.length - 1];

          newFieldErrors[fieldName] = err.msg;
          msgList.push(err.msg.replace('Value error, ', ''));
        });

        setFieldErrors(newFieldErrors);

        message.error({
          content: (
            <div className="flex flex-col gap-1">
              <strong>{tToast('newCase.checkFollowingErrors')}</strong>
              <ul className="list-disc pl-4 text-sm">
                {msgList.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 5,
        });
        return;
      }

      if (
        typeof errorDetails === 'string' &&
        errorDetails.includes('sop_instance_uid')
      ) {
        const match = errorDetails.match(/Key \(sop_instance_uid\)=\((.*?)\)/);
        const duplicateUID = match ? match[1] : null;

        if (duplicateUID) {
          const badFile = [...obFiles, ...Object.values(blindAssignments)].find(
            (f: any) =>
              f.dicomData?.sopInstanceUid === duplicateUID ||
              f.uid === duplicateUID
          );
          const fileName = badFile ? badFile.file.name : 'Unknown File';

          messageI18n.error('newCase.duplicateFileDetected', {
            fileName,
            uid: duplicateUID,
          });
        }
      }

      const errorMsg =
        resData?.message ||
        (typeof errorDetails === 'string' ? errorDetails : '') ||
        '';
      const statusCode = resData?.status === 'fail' ? 500 : null;
      console.warn('Upload error:', errorMsg, statusCode, isCreatingNew);

      if (isCreatingNew) {
        const isStorageOrServerError =
          statusCode === 500 ||
          (typeof errorMsg === 'string' &&
            (errorMsg.includes('upload') || errorMsg.includes('storage')));
        const isDuplicateError =
          typeof errorMsg === 'string' &&
          (errorMsg.includes('Exists') || errorMsg.includes('Duplicate'));

        if (isStorageOrServerError || isDuplicateError) {
          messageI18n.warning('newCase.patientRecordExists', undefined, 3);

          queryClient.invalidateQueries({ queryKey: ['patientSearch'] });

          const displayId = patForm.id;
          const recoveryPatient = {
            id: displayId,
            patient_id: displayId,
            patient_name: patForm.firstName,
            patient_last_name: patForm.lastName,
          };

          setSelectedPatient(recoveryPatient);
          setIsCreatingNew(false);
          setFieldErrors({});

          if (patForm.id || patForm.lastName) {
            setSearchTerm(patForm.id || patForm.lastName);
            if (patForm.id) setTargetPatientId(patForm.id);
          }
        } else {
          message.error(errorMsg || tToast('upload.failed'));
        }
      } else {
        message.error(errorMsg || tToast('upload.failed'));
      }
    },
  });

  const handleResetFormOnly = () => {
    setSelectedPatient(null);
    setIsCreatingNew(false);
    setSearchTerm('');
    setIsLocked(false);
    setPatForm({ firstName: '', lastName: '', id: '' });
    setPregForm({
      lmp: null,
      edd: null,
      gaWeeks: '',
      gaDays: '',
      crl: '',
      bpd: '',
      hc: '',
      ac: '',
      fl: '',
      serverGaCrl: null,
      serverGaBpd: null,
      serverGaHc: null,
      serverGaAc: null,
      serverGaFl: null,
    });
    setFieldErrors({});
  };
  const handleReset = () => {
    setObFiles([]);
    setBlindAssignments({});
    handleResetFormOnly();
    setSelectedDate(null);
    setUniquePatientIds([]);
    setUniqueDates([]);
    setTargetPatientId(null);
    setActiveTab('OB');
    setPreviewItem(null);
    setBatchMode('NONE');
    if (contextPatient) {
      applyContextPatient();
    }
  };
  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleChangePatient = () => {
    if (lockPatientIdentity) return;
    setSelectedPatient(null);
    setIsCreatingNew(false);
    if (batchMode !== 'SEPARATE') setTargetPatientId(null);
    setSearchTerm('');
    setFieldErrors({});
    console.log('Changing patient - clearing form.');
  };
  //
  const handleComplete = async () => {
    setFieldErrors({});
    const errors: Record<string, string> = {};
    if (
      filteredObFiles.length === 0 &&
      Object.keys(filteredBlindAssignments).length === 0
    )
      return messageI18n.warning('newCase.noFilesForSelection');
    if (isCreatingNew) {
      const nameRegex = /^(?!\d+$).+$/; // Regex: Must contain at least one non-digit character (not just numbers)

      // First Name Validation
      if (!patForm.firstName.trim()) {
        errors['patient_name'] = 'First name is required';
      } else if (patForm.firstName.trim().length < 3) {
        errors['patient_name'] = 'First name must be at least 3 characters';
      } else if (!nameRegex.test(patForm.firstName.trim())) {
        errors['patient_name'] = 'First name cannot be only numbers';
      }

      // Last Name Validation
      if (!patForm.lastName.trim()) {
        errors['patient_last_name'] = 'Last name is required';
      } else if (patForm.lastName.trim().length < 3) {
        errors['patient_last_name'] = 'Last name must be at least 3 characters';
      } else if (!nameRegex.test(patForm.lastName.trim())) {
        errors['patient_last_name'] = 'Last name cannot be only numbers';
      }
    } else if (!selectedPatient) {
      return messageI18n.error('newCase.selectPatient');
    }
    if (!isLocked) {
      const hasLMP = !!pregForm.lmp;
      const hasEDD = !!pregForm.edd;
      const gaW = parseInt(pregForm.gaWeeks || '0');
      if (!hasLMP && !hasEDD && gaW <= 0) {
        errors['pregnancy_dates'] = 'Required';
        errors['pregnancy_global'] = 'Provide LMP, EDD, or GA.';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return messageI18n.error('newCase.fixErrors');
    }

    const studies: any[] = [];
    if (filteredObFiles.length > 0) { 
      const type = filteredObFiles[0].type;
      const rawObDate = filteredObFiles.find((f: any) => f.dicomData?.studyDate)?.dicomData?.studyDate;
      const formattedObDate = rawObDate ? dayjs(rawObDate, 'YYYYMMDD').format('YYYY-MM-DD') : null;
      
      studies.push({
        examType: 'OB',
        studyCategory: trimester,
        study_date: formattedObDate,
        sop_instance_uids:
          type === 'dicom' ? filteredObFiles.map((f: any) => f.uid) : [],
        dicomFiles:
          type === 'dicom' ? filteredObFiles.map((f: any) => f.file) : [],
        videoFiles:
          type === 'video'
            ? filteredObFiles.map((f: any) => ({
                file: f.file,
                ...f.videoMeta,
              }))
            : [],
      });
    }
    if (Object.keys(filteredBlindAssignments).length > 0) {
      const blind = Object.values(filteredBlindAssignments);
      const type = blind[0].type;
      studies.push({
        examType: 'BLIND',
        studyCategory: trimester,
        sop_instance_uids: type === 'dicom' ? blind.map((f: any) => f.uid) : [],
        dicomFiles: type === 'dicom' ? blind.map((f: any) => f.file) : [],
        videoFiles:
          type === 'video'
            ? blind.map((f: any) => ({ file: f.file, ...f.videoMeta }))
            : [],
      });
    }

    const blindFiles = Object.values(filteredBlindAssignments);
    const params: any = {
      studies,
      convert_to_dicom:
        (filteredObFiles.length > 0 && filteredObFiles[0].type === 'video') ||
        (blindFiles.length > 0 && blindFiles[0].type === 'video'),
    };
    if (selectedPatient) params.existing_patient_id = selectedPatient.id;
    else
      params.patient_data = {
        patient_name: patForm.firstName,
        patient_last_name: patForm.lastName,
        patient_id: patForm.id || undefined,
      };

    const examDateStr = getExamDateStr();
    const datingRefStr = getExamDateStr();
    const gaW = pregForm.gaWeeks ? parseInt(pregForm.gaWeeks, 10) : 0;
    const gaD = pregForm.gaDays ? parseInt(pregForm.gaDays, 10) : 0;
    const gaTotalDays = gaW * 7 + gaD;

    let lmpStr = pregForm.lmp?.format('YYYY-MM-DD') || null;
    let eddStr = pregForm.edd?.format('YYYY-MM-DD') || null;
    let gaWeeks = gaW > 0 || gaD > 0 ? gaW : null;
    let gaDays = gaW > 0 || gaD > 0 ? gaD : 0;

    if (isHistoricalDatingMode && gaTotalDays > 0) {
      const examLmp = dayjs(examDateStr)
        .subtract(gaTotalDays, 'day')
        .startOf('day');
      lmpStr = examLmp.format('YYYY-MM-DD');
      eddStr = examLmp.add(280, 'day').format('YYYY-MM-DD');
    } else {
      const gaFromLmp = lmpStr ? computeGAFromLmp(lmpStr, datingRefStr) : null;
      if (gaFromLmp) {
        gaWeeks = gaFromLmp.weeks;
        gaDays = gaFromLmp.days;
      }
    }

    const pregnancyPayload = {
      dating_method: 'ALL_PARAMETERS' as const,
      ultrasound_date: examDateStr,
      last_menstrual_period: lmpStr,
      estimated_due_date: eddStr,
      gestational_age_weeks: gaWeeks,
      gestational_age_days: gaDays,
      preg_crl: pregForm.crl ? parseFloat(pregForm.crl) : null,
      preg_bpd: pregForm.bpd ? parseFloat(pregForm.bpd) : null,
      preg_hc: pregForm.hc ? parseFloat(pregForm.hc) : null,
      preg_ac: pregForm.ac ? parseFloat(pregForm.ac) : null,
      preg_fl: pregForm.fl ? parseFloat(pregForm.fl) : null,
    };

    if (!isLocked) {
      params.pregnancy_data = pregnancyPayload;
    } else if (selectedPatient) {
      params.pregnancy_data = pregnancyPayload;
    }
    uploadCase(params);
  };

  useEffect(() => {
    if (lastPregnancyEditRef.current !== 'lmp') return;
    if (isLocked || !pregForm.lmp || isHistoricalDatingMode) return;

    const anchor = getExamDateDayjs();
    const lmp = dayjs(pregForm.lmp).startOf('day');

    const gaW = parseInt(pregForm.gaWeeks || '0', 10);
    const gaD = parseInt(pregForm.gaDays || '0', 10);
    const gaTotalDays = gaW * 7 + gaD;
    const weeksFromStoredLmp = Math.floor(anchor.diff(lmp, 'day') / 7);

    // GA/biometry at exam date — ignore unrelated pregnancy LMP on file
    if (
      gaTotalDays > 0 &&
      gaTotalDays <= GA_LIMITS.MAX_TOTAL * 7 + 6 &&
      weeksFromStoredLmp > GA_LIMITS.MAX_TOTAL
    ) {
      return;
    }

    if (lmp.isAfter(anchor)) {
      messageI18n.warning('newCase.lmpAfterExamDate');
      return;
    }

    const totalDays = anchor.diff(lmp, 'day');
    const totalWeeks = Math.floor(totalDays / 7);

    if (totalWeeks > GA_LIMITS.MAX_TOTAL) {
      messageI18n.error('newCase.gestationalAgeExceeds42');
      setPregForm((prev) => ({
        ...prev,
        lmp: null,
        edd: null,
        gaWeeks: '',
        gaDays: '',
      }));
      setTrimester('T1');
      return;
    }

    const newEdd = lmp.add(280, 'day').startOf('day');
    const w = totalWeeks.toString();
    const d = (totalDays % 7).toString();

    setPregForm((prev) => {
      if (
        prev.gaWeeks === w &&
        prev.gaDays === d &&
        prev.edd?.isSame(newEdd, 'day')
      ) {
        return prev;
      }
      return { ...prev, edd: newEdd, gaWeeks: w, gaDays: d };
    });
  }, [
    pregForm.lmp,
    isLocked,
    isHistoricalDatingMode,
    getExamDateDayjs,
  ]);

  useEffect(() => {
    if (lastPregnancyEditRef.current !== 'edd') return;
    if (isLocked || !pregForm.edd || isHistoricalDatingMode) return;
    const anchor = getExamDateDayjs();
    const expectedLmp = dayjs(pregForm.edd).subtract(280, 'day').startOf('day');
    const totalDays = anchor.diff(expectedLmp, 'day');
    const w = Math.floor(totalDays / 7).toString();
    const d = (totalDays % 7).toString();
    if (expectedLmp.isAfter(anchor)) {
      messageI18n.warning('newCase.lmpAfterExamDate');
      return;
    }
    setPregForm((prev) => {
      if (
        prev.lmp?.isSame(expectedLmp, 'day') &&
        prev.gaWeeks === w &&
        prev.gaDays === d
      ) {
        return prev;
      }
      return {
        ...prev,
        lmp: expectedLmp,
        gaWeeks: w,
        gaDays: d,
      };
    });
  }, [pregForm.edd, isLocked, isHistoricalDatingMode, getExamDateDayjs]);

  useEffect(() => {
    if (lastPregnancyEditRef.current !== 'ga') return;
    if (isLocked) return;
    const w = parseInt(pregForm.gaWeeks || '0');
    const d = parseInt(pregForm.gaDays || '0');

    if (w > GA_LIMITS.MAX_TOTAL) {
      messageI18n.warning('newCase.gestationalAgeCapped42');
      setPregForm((prev) => ({ ...prev, gaWeeks: '42', gaDays: '0' }));
      return;
    }

    const totalDays = w * 7 + d;
    if (totalDays <= 0) return;

    const anchor = getExamDateDayjs();
    const calculatedLmp = anchor.subtract(totalDays, 'day').startOf('day');
    const calculatedEdd = calculatedLmp.add(280, 'day').startOf('day');

    setPregForm((prev) => {
      if (
        prev.lmp?.isSame(calculatedLmp, 'day') &&
        prev.edd?.isSame(calculatedEdd, 'day')
      ) {
        return prev;
      }
      return { ...prev, lmp: calculatedLmp, edd: calculatedEdd };
    });
  }, [
    pregForm.gaWeeks,
    pregForm.gaDays,
    isLocked,
    isHistoricalDatingMode,
    getExamDateDayjs,
  ]);
  const handleBiometryChange = (type, value) => {
    lastPregnancyEditRef.current = 'biometry';
    setPregForm((prev) => {
      const newForm = { ...prev };

      // Update individual biometry value
      const fieldMap = { CRL: 'crl', BPD: 'bpd', HC: 'hc', AC: 'ac', FL: 'fl' };
      newForm[fieldMap[type]] = value;

      const allBiometry = [
        newForm.crl,
        newForm.bpd,
        newForm.hc,
        newForm.ac,
        newForm.fl,
      ];
      const hasValues = allBiometry.some((val) => val !== '' && val !== null);

      if (!hasValues) {
        return { ...newForm, gaWeeks: '', gaDays: '', lmp: null, edd: null };
      }

      let calculatedTotalDays = 0;
      if (type === 'CRL' && value) {
        const res = calculateGA('CRL', value);
        if (res && !res.error) calculatedTotalDays = res.totalDays;
      } else {
        let sumDays = 0,
          count = 0;
        ['BPD', 'HC', 'AC', 'FL'].forEach((field) => {
          const val = field === type ? value : newForm[field.toLowerCase()];
          if (val) {
            const res = calculateGA(field, val);
            if (res && !res.error) {
              sumDays += res.totalDays;
              count++;
            }
          }
        });
        if (count > 0) calculatedTotalDays = sumDays / count;
      }

      if (calculatedTotalDays > 0) {
        const anchor = getExamDateDayjs();
        const dating = applyDatingFromTotalDays(calculatedTotalDays, anchor);
        Object.assign(newForm, dating);
      }
      return newForm;
    });
  };
  useEffect(() => {
    const pregnancy = getPregnancyRecord();

    if (!selectedPatient || isCreatingNew) {
      if (!selectedPatient) {
        setIsLocked(false);
        setPregForm({
          lmp: null,
          edd: null,
          gaWeeks: '',
          gaDays: '',
          crl: '',
          bpd: '',
          hc: '',
          ac: '',
          fl: '',
          serverGaCrl: null,
          serverGaBpd: null,
          serverGaHc: null,
          serverGaAc: null,
          serverGaFl: null,
        });
      }
      return;
    }

    if (!pregnancy) {
      setIsLocked(false);
      return;
    }

    setIsLocked(!!pregnancy.is_medically_active);

    const examRef = getExamDateStr();
    const lmpDayjs = pregnancy.last_menstrual_period
      ? dayjs(pregnancy.last_menstrual_period)
      : null;
    let gaWeeksStr = (
      pregnancy.current_gestational_age_weeks ?? ''
    ).toString();
    let gaDaysStr = (pregnancy.current_gestational_age_days ?? '0').toString();

    if (lmpDayjs?.isValid()) {
      const computed = computeGAFromLmp(
        lmpDayjs.format('YYYY-MM-DD'),
        examRef
      );
      if (computed) {
        gaWeeksStr = String(computed.weeks);
        gaDaysStr = String(computed.days);
      }
    }

    const lmpStr = lmpDayjs?.isValid() ? lmpDayjs.format('YYYY-MM-DD') : null;
    const canUseRecordDates =
      !isHistoricalDatingMode &&
      !!lmpStr &&
      (Boolean(pregnancy.is_medically_active) ||
        isLmpPlausibleAtReference(lmpStr, examRef, GA_LIMITS.MAX_TOTAL));

    setPregForm((prev) => {
      const hasUserBiometry =
        !!prev.crl || !!prev.bpd || !!prev.hc || !!prev.ac || !!prev.fl;
      const hasUserGa = !!prev.gaWeeks || !!prev.gaDays;

      return {
        ...prev,
        lmp: canUseRecordDates ? lmpDayjs : isHistoricalDatingMode ? prev.lmp ?? null : lmpDayjs,
        edd: canUseRecordDates
          ? pregnancy.estimated_due_date
            ? dayjs(pregnancy.estimated_due_date).startOf('day')
            : null
          : isHistoricalDatingMode
            ? prev.edd ?? null
            : pregnancy.estimated_due_date
              ? dayjs(pregnancy.estimated_due_date).startOf('day')
              : null,
        gaWeeks: hasUserGa ? prev.gaWeeks : gaWeeksStr === '0' ? '' : gaWeeksStr,
        gaDays: hasUserGa ? prev.gaDays : gaDaysStr,
        crl: hasUserBiometry
          ? prev.crl
          : pregnancy.preg_crl
            ? pregnancy.preg_crl.toString()
            : '',
        bpd: hasUserBiometry
          ? prev.bpd
          : pregnancy.preg_bpd
            ? pregnancy.preg_bpd.toString()
            : '',
        hc: hasUserBiometry
          ? prev.hc
          : pregnancy.preg_hc
            ? pregnancy.preg_hc.toString()
            : '',
        ac: hasUserBiometry
          ? prev.ac
          : pregnancy.preg_ac
            ? pregnancy.preg_ac.toString()
            : '',
        fl: hasUserBiometry
          ? prev.fl
          : pregnancy.preg_fl
            ? pregnancy.preg_fl.toString()
            : '',
      };
    });
  }, [
    pregData,
    selectedPatient,
    isCreatingNew,
    isHistoricalDatingMode,
    getExamDateStr,
    getPregnancyRecord,
  ]);

  // Locked pregnancy: fill GA at ultrasound/exam reference when API omits gestational_age_*.
  useEffect(() => {
    if (!isLocked || !pregForm.lmp) return;
    const w = parseInt(pregForm.gaWeeks || '0', 10);
    const d = parseInt(pregForm.gaDays || '0', 10);
    if (w > 0 || d > 0) return;

    const lmpStr = dayjs(pregForm.lmp).format('YYYY-MM-DD');
    const computed = computeGAFromLmp(lmpStr, getExamDateStr());
    if (!computed) return;

    setPregForm((prev) => ({
      ...prev,
      gaWeeks: String(computed.weeks),
      gaDays: String(computed.days),
    }));
  }, [
    isLocked,
    pregForm.lmp,
    pregForm.gaWeeks,
    pregForm.gaDays,
    getExamDateStr,
  ]);

  useEffect(() => {
    const weeks = parseInt(pregForm.gaWeeks || '0');

    if (weeks > 0) {
      if (weeks < 14) {
        if (trimester !== 'T1') setTrimester('T1');
      } else if (weeks < 28) {
        if (trimester !== 'T2') setTrimester('T2');
      } else {
        if (trimester !== 'T3') setTrimester('T3');
      }
    }
  }, [pregForm.gaWeeks]);
  const handleBack = () => {
    onClose();
  };

  const uploadButtonLabel =
    batchMode === 'SEPARATE' ? (
      <>
        <FiCheck className="mr-2" /> {t('oneStep.upload')}{' '}
        {patientMap[targetPatientId || ''] || t('oneStep.uploadCurrent')}
      </>
    ) : (
      <>
        <FiCheck className="mr-2" /> {t('oneStep.completeUpload')}
      </>
    );

  const wizardContent = (
    <>
      <div
        className={`font-sans transition-colors duration-200 flex flex-col relative ${
          isModalMode
            ? 'h-full bg-slate-50 dark:bg-gray-900'
            : 'min-h-screen h-full bg-slate-50 dark:bg-gray-900 p-4 lg:p-6 gap-6'
        }`}
      >
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-slate-200 dark:border-gray-700 flex justify-between items-center shrink-0 h-[72px] relative">
          <div className="flex items-center gap-4 px-6 h-full">
            <button
              type="button"
              onClick={handleBack}
              className="p-2 hover:bg-slate-50 dark:hover:bg-gray-700 rounded-lg text-slate-400 transition-colors"
              aria-label={t('oneStep.back', 'Back')}
            >
              <FiChevronLeft size={22} />
            </button>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <div className="bg-blue-600 text-white p-1 rounded-md shadow-sm">
                <FiPlus size={16} />
              </div>
              {t('oneStep.title')}
            </h2>
          </div>
          <div className="flex items-center gap-3 px-6 h-full">
            <Button
              variant="ghost"
              onClick={handleReset}
              className="text-slate-500 hover:text-red-500"
            >
              <FiTrash2 className="mr-2" /> {t('oneStep.reset')}
            </Button>
            <Button
              onClick={handleComplete}
              disabled={isUploading}
              className="bg-slate-900 text-white px-8 font-semibold shadow-lg hover:bg-slate-800 active:scale-95 transition-all"
            >
              {isUploading ? (
                <>
                  <FiLoader className="animate-spin mr-2" />{' '}
                  {t('oneStep.processing')}
                </>
              ) : (
                uploadButtonLabel
              )}
            </Button>
            {isModalMode && (
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <FiX size={24} />
              </button>
            )}
          </div>
        </div>

        {/* Main body */}
        <div
          className={`flex-1 flex flex-col xl:flex-row min-h-0 ${
            isModalMode ? 'overflow-hidden' : 'gap-6'
          }`}
        >
          {/* Left: patient + pregnancy */}
          <div className="w-full xl:w-[410px] bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 flex flex-col shrink-0 min-h-0">
            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              {batchMode === 'REGROUP' && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-3">
                  <FiAlertCircle className="text-amber-600 text-xl flex-shrink-0" />
                  <div className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>{t('oneStep.batch.regroupMode')}</strong>{' '}
                    {t('oneStep.batch.regroupModeHelp')}
                  </div>
                </div>
              )}
              {batchMode === 'SEPARATE' && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-800 animate-in slide-in-from-top-2 duration-300">
                  <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">
                    {t('oneStep.batch.multiplePatientsDetected')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {uniquePatientIds.map((id) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => switchToPatientContext(id, patientMap)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all flex items-center gap-2 ${
                          targetPatientId === id
                            ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105'
                            : 'bg-white dark:bg-gray-800 text-slate-500 border-slate-200 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <FiUserCheck size={14} />
                        {patientMap[id] ? patientMap[id].split(' ')[0] : id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <PatientIdentityForm
                selectedPatient={selectedPatient}
                isCreatingNew={isCreatingNew}
                setIsCreatingNew={setIsCreatingNew}
                patForm={patForm}
                setPatForm={setPatForm}
                fieldErrors={fieldErrors}
                patientList={patientList}
                onSearch={onSearch}
                isLoadingSearch={isSearchingPatients}
                onSelectPatient={(id: string) => {
                  const p = patientList?.items.find((x: any) => x.id === id);
                  if (p) setSelectedPatient(p);
                }}
                onChangePatient={handleChangePatient}
                uniquePatientIds={uniquePatientIds}
                patientMap={patientMap}
                targetPatientId={targetPatientId}
                setTargetPatientId={setTargetPatientId}
                setSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
                onSwitchPatient={(id: string) =>
                  switchToPatientContext(id, patientMap)
                }
                lockPatientIdentity={lockPatientIdentity}
              />
              <div
                className={`pt-8 border-t border-slate-100 dark:border-gray-700 ${
                  !selectedPatient && !isCreatingNew
                    ? 'opacity-40 pointer-events-none'
                    : ''
                }`}
              >
                <PregnancyForm
                  trimester={trimester}
                  pregForm={pregForm}
                  setPregForm={setPregForm}
                  isLmpDisabledByDate={isLmpDisabledByDate}
                  isHistoricalExamDate={isHistoricalDatingMode}
                  examDateLabel={getExamDateDayjs().format('DD/MM/YYYY')}
                  isLocked={isLocked}
                  fieldErrors={fieldErrors}
                  uniqueDates={uniqueDates}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  onBiometryChange={handleBiometryChange}
                  onPregnancyEditSource={(source) => {
                    lastPregnancyEditRef.current = source;
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right: file workspace */}
          <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 flex flex-col min-h-0 relative overflow-hidden">
            {batchMode === 'DECIDING' && (
              <BatchDecisionOverlay
                uniquePatientIds={uniquePatientIds}
                t={t}
                onSeparate={() => {
                  setBatchMode('SEPARATE');
                  if (uniquePatientIds.length > 0)
                    switchToPatientContext(uniquePatientIds[0], patientMap);
                }}
                onRegroup={() => setBatchMode('REGROUP')}
              />
            )}
            <div className="flex-1 flex flex-col min-h-0 relative">
              <FileWorkspace
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                obFiles={filteredObFiles}
                setObFiles={() => {}}
                blindAssignments={filteredBlindAssignments}
                setBlindAssignments={() => {}}
                onRemoveOb={handleRemoveOb}
                onRemoveBlind={handleRemoveBlind}
                onBlindDrop={handleBlindDrop}
                obInputRef={obInputRef}
                blindInputRef={blindInputRef}
                targetPatientId={targetPatientId}
                setPreviewItem={setPreviewItem}
                SWEEPS={SWEEPS}
                onBlindClick={handleBlindClick}
              />
            </div>
          </div>
        </div>

        <input
          type="file"
          multiple
          hidden
          ref={obInputRef}
          accept=".dcm,video/*"
          onChange={(e) =>
            handleUpload(Array.from(e.target.files || []), 'OB')
          }
        />
        <input
          type="file"
          multiple
          hidden
          ref={blindInputRef}
          accept=".dcm,video/*"
          onChange={(e) =>
            handleUpload(Array.from(e.target.files || []), 'BLIND')
          }
        />

        {previewItem && (
          <div className="fixed inset-0 z-[10000] bg-black flex flex-col animate-in fade-in duration-300">
            <div className="w-full z-50 h-24 px-8 flex items-center justify-between bg-gradient-to-b from-black/90 via-black/40 to-transparent pointer-events-none">
              <div className="flex items-center gap-4 pointer-events-auto">
                <div className="bg-white/10 p-2 rounded-lg backdrop-blur-md border border-white/10 shadow-2xl">
                  {previewItem.type === 'dicom' ? (
                    <FiFile size={20} className="text-blue-400" />
                  ) : (
                    <FiFilm size={20} className="text-purple-400" />
                  )}
                </div>
                <div className="drop-shadow-lg">
                  <p className="text-[10px] text-white/50 uppercase font-bold tracking-[0.2em] leading-tight mb-0.5">
                    Preview
                  </p>
                  <span className="font-bold text-white/90 text-sm truncate max-w-lg block">
                    {previewItem.file.name}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewItem(null)}
                className="pointer-events-auto text-white/60 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-all backdrop-blur-md border border-white/10 shadow-xl hover:rotate-90 active:scale-90"
              >
                <FiX size={28} />
              </button>
            </div>
            <div className="flex-1 w-full h-full bg-black flex items-center justify-center overflow-hidden">
              {previewItem.type === 'video' ? (
                <div className="w-full h-full">
                  <VideoViewer file={previewItem.file} />
                </div>
              ) : (
                <div className="w-full h-full">
                  <DicomViewer file={previewItem.file} />
                </div>
              )}
            </div>
          </div>
        )}

        <style>{`
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
          .path-anim { animation: draw-path 3s ease-in-out infinite alternate; }
          @keyframes draw-path { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
          @keyframes sweep-1 {
            0%   { left: 45%; top: 90%; transform: rotate(90deg); opacity: 1; }
            30%  { left: 45%; top: 60%; transform: rotate(90deg); opacity: 1; }
            35%  { left: 85%; top: 60%; transform: rotate(0deg); opacity: 0; }
            40%  { left: 85%; top: 60%; transform: rotate(0deg); opacity: 1; }
            100% { left: 15%; top: 60%; transform: rotate(0deg); opacity: 1; }
          }
          .ultrasound-sweep-1 { animation: sweep-1 4s ease-in-out infinite alternate; }
          @keyframes sweep-v { 0% { top: 35%; opacity: 0.8; transform: rotate(90deg)} 100% { top: 85%; opacity: 1; transform: rotate(90deg)} }
          .ultrasound-sweep-2 { left: 20%; animation: sweep-v 2s ease-in-out infinite alternate; }
          .ultrasound-sweep-3 { left: 45%; animation: sweep-v 2s ease-in-out infinite alternate 0.3s; }
          .ultrasound-sweep-4 { left: 70%; animation: sweep-v 2s ease-in-out infinite alternate 0.6s; }
          @keyframes sweep-h { 0% { left: 15%; opacity: 0.8; } 100% { left: 85%; opacity: 1; } }
          .ultrasound-sweep-5 { top: 45%; animation: sweep-h 2.5s ease-in-out infinite alternate; }
          .ultrasound-sweep-6 { top: 70%; animation: sweep-h 2.5s ease-in-out infinite alternate 0.5s; }
          @keyframes sweep-c { 0% { left: 20%; top: 75%; } 50% { left: 50%; top: 85%; } 100% { left: 80%; top: 75%; } }
          .ultrasound-sweep-7 { animation: sweep-c 3s ease-in-out infinite alternate; }
        `}</style>
      </div>
    </>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      {isModalMode ? (
        <Modal isOpen={isOpen} onClose={handleClose} size="5xl">
          {wizardContent}
        </Modal>
      ) : (
        wizardContent
      )}
    </DndProvider>
  );
};
